import { Link, useNavigate, useParams } from "react-router-dom"
import InnerScreenHeader from "@/components/layout/inner-screen-header"
import { Card, CardContent } from "@/components/ui/card"
import {
  CustomerAddress,
  useCustomer,
  useCustomersActions,
} from "@/api/customers"
import Loader from "@/components/layout/loader"
import { Button } from "@/components/ui/button"
import { CustomersEditSheet } from "./customers-edit.sheet"
import { toast } from "sonner"
import { Country, State } from "@/api/data"
import ButtonCopy from "@/components/ui/button-copy"
import { AlertDialog, AlertDialogTrigger } from "@/components/ui/alert-dialog"
import { ConfirmDialog } from "@/components/ux/confirm.dialog"

const AddressBlock = ({
  address,
}: {
  address: CustomerAddress & { state?: State; country: Country };
}) => (
  <div className="flex flex-col pl-4">
    <div>
      {address.address}
      {address.addressExtra && `, ${address.addressExtra}`}
    </div>
    <div>
      {address.city}, {address.postalCode}
    </div>
    <div>
      {address.state && `${address.state.name}, `}
      {address.country.name}
    </div>
  </div>
)

export default function CustomerViewScreen() {
  const { id, mode } = useParams()
  const navigate = useNavigate()
  const { customer } = useCustomer(id!)
  const { remove } = useCustomersActions()

  const onRemove = async () => {
    await remove(id!)
    navigate("/customers")
    toast.success("Customer removed")
  }

  if (!customer) {
    return <Loader message="Loading customer..." />
  }

  return (
    <div className="flex flex-col gap-4">
      <InnerScreenHeader title="Customers" to="/customers" />
      <Card className="shadow-sm">
        <CardContent className="gap-2">
          <div className="text-xl font-semibold">
            {customer.firstName} {customer.lastName}
          </div>
          <div className="text-muted-foreground">{customer.email}</div>
          <div className="flex flex-row gap-4 pt-3">
            <Button variant="outline" asChild>
              <Link to={`/customers/${id}/edit`}>Edit Information</Link>
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline">Remove</Button>
              </AlertDialogTrigger>
              <ConfirmDialog
                title="Remove customer"
                description={`Are you sure you want to remove ${customer.firstName} ${customer.lastName}?`}
                onConfirm={onRemove}
              />
            </AlertDialog>
          </div>
        </CardContent>
      </Card>
      <Card className="shadow-sm">
        <CardContent>
          <div className="flex flex-col md:flex-row text-sm gap-4">
            <div className="flex flex-col gap-3 md:w-6/12">
              <div className="flex">
                <div className="text-muted-foreground w-4/12 pr-2">
                  First Name
                </div>
                <div>{customer.firstName}</div>
              </div>
              <div className="flex">
                <div className="text-muted-foreground w-4/12 pr-2">
                  Last Name
                </div>
                <div>{customer.lastName}</div>
              </div>
              <div className="flex">
                <div className="text-muted-foreground w-4/12 pr-2">
                  Organization's Name
                </div>
                <div>{customer.organizationName}</div>
              </div>
              <div className="flex">
                <div className="text-muted-foreground w-4/12 pr-2">Email</div>
                <div className="flex items-center gap-2">
                  {customer.email} <ButtonCopy value={customer.email} />{" "}
                </div>
              </div>
              <div className="flex">
                <div className="text-muted-foreground w-4/12 pr-2">Tax ID</div>
                <div>{customer.taxId}</div>
              </div>
              <div className="flex">
                <div className="text-muted-foreground w-4/12 pr-2">
                  Phone Number
                </div>
                <div>{customer.phoneNumber}</div>
              </div>
            </div>
            <div className="flex flex-col gap-2 md:w-6/12">
              {customer.addresses.length > 0 && (
                <>
                  <div className="text-muted-foreground">Shipping Address</div>
                  <AddressBlock address={customer.addresses[0]} />
                </>
              )}
              {customer.addresses.length > 1 && (
                <>
                  <div className="text-muted-foreground">Billing Address</div>
                  <AddressBlock address={customer.addresses[1]} />
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      <CustomersEditSheet
        id={id}
        open={mode === "edit"}
        onDone={() => navigate(`/customers/${id}`)}
      />
    </div>
  )
}
